/**
 * WritingCoursesHub.jsx — Scribe Training Courses hub.
 *
 * 12 modules across 3 tiers (Foundation / Intermediate / Advanced),
 * with level-gated unlocks at Scribe Level 1 / 10 / 25. Mirrors the
 * tiered layout of the Scribe Training course bible.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PenLine, CheckCircle2, Lock, ChevronRight, Loader2, Sparkles,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { authFetch } from '../utils/authUtils';
import WriterStatsCard from '../components/WriterStatsCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WritingCoursesHub = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/api/writer/courses/catalog`);
        if (r.ok) setCat(await r.json());
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-300">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading courses…
      </div>
    );
  }
  if (!cat) return null;

  // Group modules by tier.
  const byTier = {};
  for (const m of cat.modules) {
    byTier[m.tierId] = byTier[m.tierId] || [];
    byTier[m.tierId].push(m);
  }
  for (const t in byTier) byTier[t].sort((a, b) => a.order - b.order);

  const writerLevel = cat.viewer?.writerLevel || 1;

  return (
    <main className="max-w-5xl mx-auto px-4 py-8" data-testid="writing-courses-hub">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-2">
          <PenLine className="w-6 h-6 text-fuchsia-400" /> Scribe Training
        </h1>
        <p className="text-sm text-gray-400 mt-1">
          Twelve modules across three tiers. Every pass boosts your Scribe XP and adds cashable coins.
        </p>
      </div>

      <div className="mb-6">
        <WriterStatsCard />
      </div>

      <div className="grid grid-cols-1 gap-6">
        {cat.tiers.map((t) => {
          const modules = byTier[t.id] || [];
          const prog = cat.progress?.[t.id] || { completed: 0, total: modules.length, pct: 0, unlocked: false };
          const tierLocked = !prog.unlocked;
          return (
            <Card
              key={t.id}
              className="bg-slate-800/50 border-slate-700 overflow-hidden"
              data-testid={`writer-tier-${t.id}`}
            >
              <div
                className="p-4 flex items-center gap-3 border-b border-slate-700/60"
                style={{ background: `linear-gradient(90deg, ${t.colour}22, transparent)` }}
              >
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${t.colour}22`, border: `1px solid ${t.colour}66` }}
                >
                  {tierLocked ? (
                    <Lock className="w-5 h-5" style={{ color: t.colour }} />
                  ) : (
                    <Sparkles className="w-5 h-5" style={{ color: t.colour }} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-base font-bold text-white">{t.name} Tier</h3>
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ backgroundColor: `${t.colour}22`, color: t.colour, border: `1px solid ${t.colour}55` }}>
                      Unlocks at L{t.unlockLevel}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 line-clamp-1 mt-0.5">{t.description}</p>
                </div>
                <div className="text-right shrink-0">
                  <div
                    className="text-xs font-bold"
                    style={{ color: t.colour }}
                    data-testid={`writer-tier-progress-${t.id}`}
                  >
                    {prog.completed}/{prog.total} · {prog.pct}%
                  </div>
                  <div className="w-24 h-1.5 bg-slate-900/60 rounded-full overflow-hidden mt-1">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{ width: `${prog.pct}%`, backgroundColor: t.colour }}
                    />
                  </div>
                </div>
              </div>

              <CardContent className="p-3">
                {tierLocked && (
                  <div className="mb-3 px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700 text-xs text-gray-300">
                    Reach <span className="font-bold text-white">Scribe Level {t.unlockLevel}</span> to unlock this tier. You're currently Level {writerLevel}.
                  </div>
                )}
                <ul className="space-y-2">
                  {modules.map((m) => {
                    const locked = !!m.locked;
                    return (
                      <li key={m.id}>
                        <button
                          type="button"
                          disabled={locked}
                          onClick={() => navigate(`/writer-courses/lesson/${m.id}`)}
                          data-testid={`writer-lesson-row-${m.id}`}
                          className={`w-full text-left flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                            locked
                              ? 'bg-slate-900/40 border-slate-800 opacity-50 cursor-not-allowed'
                              : m.completed
                              ? 'bg-emerald-500/5 border-emerald-500/30 hover:bg-emerald-500/10'
                              : 'bg-slate-900/40 border-slate-700 hover:border-slate-500'
                          }`}
                        >
                          <div
                            className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                              m.completed
                                ? 'bg-emerald-500 text-white'
                                : locked
                                ? 'bg-slate-700 text-gray-500'
                                : 'bg-slate-700 text-gray-200'
                            }`}
                          >
                            {m.completed ? <CheckCircle2 className="w-4 h-4" /> : locked ? <Lock className="w-3.5 h-3.5" /> : m.order}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-white truncate">{m.title}</div>
                            <div className="text-[11px] text-gray-400 truncate">
                              {m.path} · {m.lessonCount} lessons · {m.quizCount}-question quiz · +{m.xp} XP · +{m.coins} coins (cashable)
                            </div>
                          </div>
                          {!locked && <ChevronRight className="w-4 h-4 text-gray-500 shrink-0" />}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </main>
  );
};

export default WritingCoursesHub;
